import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { ExpertSubmission } from "@/components/ExpertSubmission";
import { Seo } from "@/components/Seo";

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { http.get("/blog").then((r) => setPosts(r.data)); }, []);
  return (
    <div className="container-x py-16" data-testid="blog-page">
      <Seo title="Le blog — regards d'experts" description="Articles d'experts de la petite enfance : développement de l'enfant, pratiques professionnelles, neurosciences appliquées au quotidien." />
      <div className="max-w-3xl mb-12">
        <div className="eyebrow mb-3">Ressources</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">Blog - Regards d'experts</h1>
        <p className="mt-4 text-slate-600 leading-relaxed text-lg">Des articles simples et concrets, pensés pour les professionnels de la petite enfance : nos experts partagent des conseils pour enrichir vos pratiques au quotidien.</p>
        <p className="mt-3 text-sm font-semibold text-brand-700">Une communauté professionnelle, un accès gratuit, des contenus conçus pour enrichir les pratiques.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mb-12" data-testid="community-spaces">
        <div className="card-soft p-8 relative overflow-hidden" data-testid="member-space-card">
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-brand-500/10 blur-2xl pointer-events-none" />
          <ul className="space-y-5 text-slate-800">
            <li>
              <a href="#articles" data-testid="link-articles" className="group flex items-start gap-3 font-semibold hover:text-brand-700">
                <CheckCircle2 size={20} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>Accéder aux articles et conseils des experts <ArrowUpRight size={14} strokeWidth={2} className="inline transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </a>
            </li>
            <li>
              <Link to="/formations" data-testid="link-formations" className="group flex items-start gap-3 font-semibold hover:text-brand-700">
                <CheckCircle2 size={20} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>Découvrir les formations Beehive <ArrowUpRight size={14} strokeWidth={2} className="inline transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </Link>
            </li>
          </ul>
        </div>
        <div className="card-soft p-8 relative overflow-hidden bg-slate-900 border-slate-900 text-white" data-testid="expert-space-card">
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-brand-500/25 blur-2xl pointer-events-none" />
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-300 mb-2">Espace experts</div>
          <h2 className="font-display font-bold text-2xl">Rejoindre le réseau d'experts</h2>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">Vous êtes expert de la petite enfance, du développement de l'enfant ou d'un domaine associé ? Rejoignez le réseau d'experts de Beehive Academy et partagez votre expertise à travers nos formations.</p>
          <Link to="/experts" data-testid="expert-space-cta" className="btn-primary mt-6 text-sm">Déposer mon profil</Link>
        </div>
      </div>

      <ExpertSubmission />
      <div id="articles" className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group card-soft overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg" style={{ transitionProperty: "transform, box-shadow" }}>
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                <span className="flex items-center gap-1"><Clock size={12} strokeWidth={1.5} />{p.reading_time} min</span>
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">Lire <ArrowUpRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
